import { useEffect, useState } from "react";
import TermsViewer from "./TermsViewer";
import { Link } from "react-router-dom";

/** Reusable acceptance block used inside signup forms and re-acceptance modals.
 *  Renders the role-specific T&C inline + Privacy Policy in a tabbed viewer,
 *  followed by role-specific mandatory consent checkboxes.
 *
 *  Emits { accepted: bool, checkboxes: [] } to parent via onChange.
 *  Do NOT rely on this alone — backend enforces the same gates on register. */
export default function TermsAcceptBlock({ role, onChange, initial = {} }) {
  const [tab, setTab] = useState(role);
  const [terms, setTerms] = useState(initial.terms || false);
  const [warranty, setWarranty] = useState(initial.warranty || false);
  const [vendorFileUse, setVendorFileUse] = useState(initial.vendorFileUse || false);
  const [privacy, setPrivacy] = useState(initial.privacy || false);

  // Compose checkbox list expected by the backend.
  useEffect(() => {
    const cb = [];
    if (terms) cb.push(`${role}_terms`);
    if (privacy) cb.push("privacy");
    if (role === "seller" && warranty) cb.push("seller_ip_warranty");
    if (role === "cam_vendor") {
      cb.push("vendor_terms");
      if (vendorFileUse) cb.push("vendor_file_use");
    }
    const required =
      role === "seller"
        ? terms && warranty && privacy
        : role === "cam_vendor"
        ? terms && vendorFileUse && privacy
        : terms && privacy;
    onChange && onChange({ accepted: required, checkboxes: cb });
  }, [role, terms, warranty, vendorFileUse, privacy]);

  return (
    <div className="mt-4 space-y-3" data-testid={`accept-block-${role}`}>
      <div className="flex gap-2 text-xs font-mono border-b border-border">
        <button
          type="button"
          data-testid={`accept-tab-${role}`}
          onClick={() => setTab(role)}
          className={`px-3 py-2 border-b-2 -mb-px ${tab === role ? "border-primary text-primary" : "border-transparent text-muted-foreground"}`}
        >
          {role === "cam_vendor" ? "CAM Vendor T&C" : role === "seller" ? "Seller T&C" : "Buyer T&C"}
        </button>
        <button
          type="button"
          data-testid="accept-tab-privacy"
          onClick={() => setTab("privacy")}
          className={`px-3 py-2 border-b-2 -mb-px ${tab === "privacy" ? "border-primary text-primary" : "border-transparent text-muted-foreground"}`}
        >
          Privacy Policy
        </button>
      </div>

      <TermsViewer role={tab} />

      <div className="space-y-2 pt-1">
        <label className="flex items-start gap-2 text-sm" data-testid={`chk-${role}-terms`}>
          <input
            type="checkbox"
            checked={terms}
            onChange={(e) => setTerms(e.target.checked)}
            className="mt-1"
            data-testid={`chk-${role}-terms-input`}
          />
          <span>
            I have read, understood, and agree to be bound by the{" "}
            <Link to={`/terms/${role === "cam_vendor" ? "cam-vendor" : role}`} target="_blank" className="text-primary underline">
              Jeweltix {role === "cam_vendor" ? "CAM Vendor" : role === "seller" ? "Seller" : "Buyer"} Terms & Conditions
            </Link>{" "}
            and the{" "}
            <Link to="/privacy" target="_blank" className="text-primary underline">Privacy Policy</Link>.
          </span>
        </label>

        {role === "seller" && (
          <label className="flex items-start gap-2 text-sm" data-testid="chk-seller-warranty">
            <input
              type="checkbox"
              checked={warranty}
              onChange={(e) => setWarranty(e.target.checked)}
              className="mt-1"
              data-testid="chk-seller-warranty-input"
            />
            <span>
              I confirm that I am the original creator and lawful owner of all designs I upload, and that they do not infringe any third party's intellectual property rights.
            </span>
          </label>
        )}

        {role === "cam_vendor" && (
          <label className="flex items-start gap-2 text-sm" data-testid="chk-vendor-file-use">
            <input
              type="checkbox"
              checked={vendorFileUse}
              onChange={(e) => setVendorFileUse(e.target.checked)}
              className="mt-1"
              data-testid="chk-vendor-file-use-input"
            />
            <span>
              I understand that I receive design files strictly for single-job fulfilment, that I may not retain, reuse, copy or share them, and that I am an independent business, not an employee or agent of Jeweltix.
            </span>
          </label>
        )}

        <label className="flex items-start gap-2 text-sm" data-testid="chk-privacy">
          <input
            type="checkbox"
            checked={privacy}
            onChange={(e) => setPrivacy(e.target.checked)}
            className="mt-1"
            data-testid="chk-privacy-input"
          />
          <span>I have read and accept the Privacy Policy and consent to the processing of my personal data as described.</span>
        </label>
      </div>
    </div>
  );
}
